<template>
    <RegisterStep
        :ready="ready"
        :loading="loading"
        :previous-route="previousRoute"
        v-bind="$attrs"
        @submit="handleStepSubmitted"
    >

        <template v-slot:title>
            Mode / engagement de paiement
        </template>

        <template v-slot:aside>
            <h3 class="h6 mb-0">Total : <strong>{{ order.current_total_formatted }}</strong></h3>
        </template>

        <template v-if="ready">
            <div class="checkout-page__text mb-4 pb-2">
                <div class="mb-2">
                    Votre inscription sera prise en compte dès la commande, accompagnée de votre paiement (ou d’une pièce justificative, selon)
                </div>
                <div>
                    Moyen(s) de paiement :
                </div>
            </div>

            <div style="max-width: 600px">
                <div class="mb-4 pb-2">
                    <b-collapse :visible="!editingPaymentModes">
                        <h4 class="cursus-register__form-title mb-3">Mode de paiement</h4>
                        <div class="card">
                            <div class="card-body py-3">
                                <div class="row align-items-center">
                                    <div class="col">
                                        <strong>{{ paymentModeLabel }}</strong>
                                    </div>
                                    <div class="col-auto">
                                        <button class="btn btn-link" @click="editingPaymentModes = true">
                                            Modifier...
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </b-collapse>
                    <b-collapse :visible="editingPaymentModes">
                        <Field class="mb-0" name="payment_mode" :errors="errors">
                            <b-form-radio-group
                                class="cursus-radios"
                                v-model="currentOrder.payment_mode"
                                stacked
                                @change="handlePaymentModeChanged"
                                @click.native="handleRadioClicked"
                            >
                                <template v-if="
                                    course.allowed_payment_modes.includes('online')
                                    || course.allowed_payment_modes.includes('bank_transfer')
                                    || course.allowed_payment_modes.includes('bank_check')
                                ">
                                    <div class="card mb-3">
                                        <template v-if="course.allowed_payment_modes.includes('bank_transfer') || course.allowed_payment_modes.includes('bank_check')">
                                            <div class="card-header">
                                                <h4 class="fs-6 mb-1">
                                                    réservé aux structures et professionnels indépendants
                                                </h4>
                                                <div class="text-muted fs-7">
                                                    (règlement à effectuer dès la commande quel que soit le mode de paiement)
                                                </div>
                                            </div>
                                        </template>
                                        <div class="card-body">
                                            <template v-if="course.allowed_payment_modes.includes('online')">
                                                <b-form-radio value="online">
                                                    paiement par <strong>carte bancaire</strong>
                                                </b-form-radio>
                                            </template>

                                            <template v-if="course.allowed_payment_modes.includes('bank_transfer')">
                                                <b-form-radio value="bank_transfer">
                                                    paiement par <strong>virement</strong>
                                                </b-form-radio>
                                            </template>

                                            <template v-if="course.allowed_payment_modes.includes('bank_check')">
                                                <b-form-radio value="bank_check">
                                                    paiement par <strong>chèque</strong>
                                                </b-form-radio>
                                            </template>
                                        </div>
                                    </div>
                                </template>

                                <template v-if="currentOrder.account_type === 'organization' && course.allowed_payment_modes.includes('obligation')">
                                    <div class="card mb-3">
                                        <div class="card-header">
                                            <h4 class="fs-6 mb-0">
                                                réservé aux collectivités, établissements publics
                                            </h4>
                                        </div>

                                        <div class="card-body">
                                            <b-form-radio value="obligation">
                                                paiement en différé par <strong>virement</strong> ou <strong>mandat administratif</strong>
                                            </b-form-radio>
                                        </div>
                                    </div>
                                </template>

                                <template v-if="course.allowed_payment_modes.includes('third_party_funding')">
                                    <div class="card">
                                        <div class="card-header">
                                            <h4 class="fs-6 mb-0">
                                                prise en charge par un organisme tiers <span class="small">(AFDAS, Pôle Emploi, …)</span>
                                            </h4>
                                        </div>
                                        <div class="card-body">
                                            <b-form-radio value="third_party_funding">
                                                paiement en différé par <strong>virement</strong>
                                            </b-form-radio>
                                        </div>
                                    </div>
                                </template>
                            </b-form-radio-group>
                        </Field>
                    </b-collapse>

                    <PaymentInformations
                        :class="editingPaymentModes ? 'mt-4' : 'border-top-0'"
                        :order="currentOrder"
                    />
                </div>

                <template v-if="currentOrder.payment_mode">
                    <div class="mb-3">
                        <template v-if="['online', 'bank_transfer', 'bank_check'].includes(currentOrder.payment_mode)">
                            <InvoiceAddressForm
                                v-model="currentOrder.invoice_address"
                                :errors="errors"
                            />
                        </template>

                        <template v-if="currentOrder.payment_mode === 'third_party_funding'">
                            <div class="mb-3">
                                <label for="attachment">
                                    Justificatif de prise en charge
                                </label>
                                <Upload
                                    id="attachment"
                                    v-model="currentOrder.attachment.file"
                                    :upload-url="attachmentUploadUrl"
                                    @input="handleUploadChanged"
                                />
                            </div>

                            <div class="alert alert-warning" style="font-size: .875rem">
                                Joignez votre pièce justificative ci-dessus, ou envoyez la par email dans un délai de 7 jours à l’adresse
                                <a href="mailto:formations@culturegrandest.fr">formations@culturegrandest.fr</a>,
                                en précisant la référence de la commande. À défaut votre inscription sera caduque.
                            </div>
                        </template>

                        <template v-if="currentOrder.payment_mode === 'obligation'">
                            <div class="mb-4">
                                <h4 class="cursus-register__form-title mb-3">Justificatif d’engagement</h4>

                                <div class="card">
                                    <div class="card-body">
                                        <label class="mb-2" for="attachment">
                                            Bon de commande, lettre d’engagement, convention, ...
                                        </label>
                                        <Upload
                                            id="attachment"
                                            v-model="currentOrder.attachment.file"
                                            :upload-url="attachmentUploadUrl"
                                            @input="handleUploadChanged"
                                        />
                                    </div>
                                    <div class="alert alert-warning border-0 mb-0 fs-7">
                                        <p class="mb-2">
                                            Joignez votre pièce justificative ci-dessus.
                                        </p>
                                        <p class="mb-2">
                                            A défaut, envoyez le document par mail, dans un délai maximum de 7 jours
                                            après la commande à l’adresse <a href="mailto:formations@culturegrandest.fr">formations@culturegrandest.fr</a>,
                                            en y précisant le numéro de commande.
                                        </p>
                                        <p class="mb-2">
                                            Ce justificatif devra nous parvenir impérativement avant le début de la formation.
                                        </p>
                                        <p class="mb-2">
                                            Passé ce délai, votre commande ne pourra plus être prise en considération et sera annulée.
                                        </p>
                                        <div class="my-3" style="border-top: 1px solid rgba(0,0,0,.125)"></div>
                                        <p class="mb-0">
                                            <strong>Chorus pro :</strong><br>
                                            Votre pièce justificative devra comprendre tous les éléments nécessaires à la réalisation et au dépôt de la facture sur Chorus Pro (N° de SIRET, Code service, N° d’engagement, ...).
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <InvoiceAddressForm
                                v-model="currentOrder.invoice_address"
                                :errors="errors"
                            />
                        </template>

                        <Field class="mb-0" name="attachment" :errors="errors">
                            <div class="d-none"></div>
                        </Field>
                    </div>
                </template>
            </div>

        </template>
    </RegisterStep>
</template>

<script>
  import { clone } from "../../../util";
  import { BFormRadio, BFormRadioGroup } from 'bootstrap-vue';
  import RegisterStep from "../RegisterStep.vue";
  import Upload from "../../Upload.vue";
  import InvoiceAddressForm from "./checkout/InvoiceAddressForm.vue";
  import Field from "../../Field.vue";
  import PaymentInformations from "./checkout/PaymentInformations.vue";
  import { getAccount, postOrderPayment, postOrderUploadUrl } from "../../../api";
  import { paymentModeLabel, paymentModes } from "../../../util/register";
  import { baseUrl } from "../../../util/config";

  export default {
        components: {
            PaymentInformations,
            Field,
            InvoiceAddressForm,
            RegisterStep,
            BFormRadio,
            BFormRadioGroup,
            Upload,
        },
        props: {
            order: Object,
            course: Object,
            session: Object,
            nextRoute: Object,
            previousRoute: Object,
            user: Object,
        },
        data() {
            return {
                ready: false,
                currentOrder: clone(this.order),
                loading: false,
                errors: null,
                editingPaymentModes: !this.order.payment_mode,
            }
        },
        computed: {
            attachmentUploadUrl() {
                const { courseId, sessionId, orderId } = this.$route.params;
                return postOrderUploadUrl({ courseId, sessionId, orderId });
            },
            paymentModeLabel() {
                return this.currentOrder.payment_mode
                    ? paymentModeLabel(this.currentOrder.payment_mode)
                    : null;
            }
        },
        methods: {
            baseUrl,
            handleStepSubmitted() {
                const { courseId, sessionId, orderId } = this.$route.params;
                this.loading = true;
                this.errors = null;
                postOrderPayment({ courseId, sessionId, orderId, data:this.currentOrder })
                    .then(order => {
                        this.currentOrder = order;
                        this.$emit('update:order', order);
                        this.$router.push(this.nextRoute);
                    })
                    .catch(error => {
                        if(error.response?.status === 422) {
                            this.errors = error.response.data.errors;
                        } else {
                            return Promise.reject(error);
                        }
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            },
            handlePaymentModeChanged() {
                this.currentOrder.attachment = {
                    file: null
                };
                this.errors = null;
                this.editingPaymentModes = false;
            },
            handleRadioClicked(e) {
                if(e.target.matches('input')) {
                    this.editingPaymentModes = false;
                }
            },
            handleUploadChanged() {
                this.currentOrder.attachment.uploaded = true;
            },
            async init() {
                const account = await getAccount({ accountId: this.currentOrder.account_id })
                    .catch(error => {});

                const { payment_mode, invoice_address, attachment } = this.currentOrder;
                this.currentOrder = {
                    ...this.currentOrder,
                    payment_mode: payment_mode ?? null,
                    invoice_address: {
                        ...account?.address,
                        name: account?.is_organization
                            ? account.organization_name
                            : this.user.full_name,
                        organization_service: account?.organization_service,
                        ...invoice_address,
                    },
                    attachment: {
                        file: null,
                        ...attachment,
                    },
                }
                if(!paymentModes().some(mode => mode.value === this.currentOrder.payment_mode)) {
                    this.currentOrder.payment_mode = null;
                }
                this.ready = true;
            },
        },
        created() {
            this.init();
        }
    }
</script>
